import cn from 'classnames'
import React, { useEffect, useMemo, useState } from 'react'
import { useController, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import { Button } from '@mui/base'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  Typography,
  useMediaQuery
} from '@mui/material'

import { errorCatch } from '../../api/helpers'
import PriceBreakdownIcon from '../../assets/icons/PriceBreakdownIcon'
import SelectAngleIcon from '../../assets/icons/SelectAngleIcon'
import Autocomplete from '../../components/Autocomplete'
import CustomButton from '../../components/Button'
import Form from '../../components/Form'
import PriceBreakdown from '../../components/PriceBreakdown'
import Select from '../../components/Select'
import Input from '../../components/TextField'
import { countries } from '../../constants/participantDetailsData'
import { emailRegex } from '../../helpers/validate'
import { OrderService } from '../../services/order'
import { addAcademicDetails, addBillingAddress, addMailConsent } from '../../store/enrolment/enrolment.actions'
import { removeCoupon } from '../../store/enrolment/enrolment.actions'
import {
  selectBillingAddress,
  selectMainMainOrderId,
  selectOrder,
  selectStudentDetails
} from '../../store/enrolment/enrolment.selectors'
import { safePushToDL } from '../../utils/saveToDataLayer'
import { WrapperForm } from './styled'

import { useDispatch, useSelector } from 'react-redux'

const PaymentScreen = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const billingAddress = useSelector(selectBillingAddress)
  const currentStudentDetails = useSelector(selectStudentDetails)
  const order = useSelector(selectOrder)

  const isSmallScreen = useMediaQuery('(max-width: 1024px)')

  const initialState = useMemo(
    () => ({
      ...billingAddress
    }),
    [billingAddress]
  )

  const initialAcademicState = useMemo(() => ({ ...currentStudentDetails }), [currentStudentDetails])

  const orderId = useSelector(selectMainMainOrderId)
  const [isLoading, setIsLoading] = useState(false)
  const [checkboxValue, setCheckboxValue] = useState(false)

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({ mode: 'onChange', defaultValues: initialState })

  const academicFormState = useForm({ mode: 'onChange', defaultValues: initialAcademicState })
  const {
    setError: setAcademicError,
    clearErrors: clearAcademicErrors,
    formState: { errors: academicErrors }
  } = academicFormState

  const { field: country } = useController({
    name: 'country',
    control: control,
    rules: {
      required: { value: true, message: 'This field is required' }
    }
  })

  const { field: firstName } = useController({
    name: 'firstName',
    control: control,
    rules: {
      required: { value: true, message: 'This field is required' }
    }
  })

  const { field: lastName } = useController({
    name: 'lastName',
    control: control,
    rules: {
      required: { value: true, message: 'This field is required' }
    }
  })

  const { field: firstLineAddress } = useController({
    name: 'firstLineAddress',
    control: control,
    rules: {
      required: { value: true, message: 'This field is required' }
    }
  })

  const { field: secondLineAddress } = useController({
    name: 'secondLineAddress',
    control: control
  })

  const { field: city } = useController({
    name: 'city',
    control: control,
    rules: {
      required: { value: true, message: 'This field is required' }
    }
  })

  const { field: county } = useController({
    name: 'province',
    control: control,
    rules: {
      required: { value: true, message: 'This field is required' }
    }
  })

  const { field: postCode } = useController({
    name: 'postcode',
    control: control,
    rules: {
      required: { value: true, message: 'This field is required' }
    }
  })

  const { field: email } = useController({
    name: 'email',
    control: control,
    rules: {
      required: { value: true, message: 'This field is required' },
      pattern: {
        value: emailRegex,
        message: 'Invalid email address'
      }
    }
  })

  const { field: consultantFullName } = useController({
    name: 'nameOfConsultantOrAdvisor',
    control: academicFormState.control,
    rules: {
      required: { value: true, message: 'This field is required' }
    }
  })

  const { field: consultantRole } = useController({
    name: 'roleOfConsultant',
    control: academicFormState.control,
    rules: {
      required: { value: true, message: 'This field is required' }
    }
  })

  const handleReceiveEmails = event => {
    setCheckboxValue(prev => !prev)
    dispatch(
      addMailConsent({
        orderId,
        body: JSON.stringify({
          receiveUpdates: event.target.checked
        })
      })
    )
  }

  const getDateFormPreferredArray = dateArray => {
    return dateArray.map(({ year, month }) => `${month} ${year}`).join(', ')
  }

  const submitAcademicDetails = data => {
    return dispatch(
      addAcademicDetails({
        orderId,
        body: {
          ...data,
          schoolName: data.placesSchoolData?.schoolName,
          schoolCity: data.placesSchoolData?.schoolCity,
          googlePlacesId: data.placesSchoolData?.googlePlacesId,
          schoolCountry: data.placesSchoolData?.schoolCountry
        }
      })
    ).unwrap()
  }

  const validateAcademicFields = () => {
    const academicValues = academicFormState.getValues()
    const isRoleFilled = Boolean(academicValues?.roleOfConsultant?.trim())
    const isConsultantNameFilled = Boolean(academicValues?.nameOfConsultantOrAdvisor?.trim())

    if (!isRoleFilled) {
      setAcademicError('roleOfConsultant', { type: 'manual', message: 'This field is required' })
    } else {
      clearAcademicErrors('roleOfConsultant')
    }

    if (!isConsultantNameFilled) {
      setAcademicError('nameOfConsultantOrAdvisor', { type: 'manual', message: 'This field is required' })
    } else {
      clearAcademicErrors('nameOfConsultantOrAdvisor')
    }

    if (!isRoleFilled || !isConsultantNameFilled) {
      return false
    }

    return true
  }

  const onSubmit = async data => {
    const isAcademicValid = validateAcademicFields()
    if (!isAcademicValid) {
      return
    }

    const { email, ...other } = data

    setIsLoading(true)
    try {
      await submitAcademicDetails(academicFormState.getValues())
    } catch (error) {
      toast.error('Failed to update student details')
      setIsLoading(false)
      return
    }

    const allProducts = []

    order.productOrders.forEach(product => {
      allProducts.push(product)

      if (product.childProductOrder) {
        allProducts.push(product.childProductOrder)
      }
    })

    safePushToDL({ ecommerce: null })
    safePushToDL(
      window.dataLayer.push({
        event: 'add_payment_info',
        ecommerce: {
          currency: 'GBP',
          value: order.amountPaid,
          coupon: order.productOrders[0].coupon?.title || '',
          payment_type: 'Credit Card',
          items: allProducts.map(product => {
            return {
              item_id: product.id,
              item_name: product.product.title,
              affiliation: 'Immerse Education Website',
              coupon: product?.coupon ? product.coupon.title : '',
              discount: product.price.programDiscount,
              index: 0,
              item_brand: product.package.packageTitle,
              item_category: product.location.title,
              item_category2: product.educationalModel,
              item_category3: product.subject.title,
              item_category4: product.package.packageTitle,
              item_variant: product.programDate
                ? product.programDate.title
                : product.preferredMonths
                  ? getDateFormPreferredArray(product.preferredMonths)
                  : '',
              price: product.price.programFee,
              quantity: 1
            }
          })
        }
      })
    )

    dispatch(
      addBillingAddress({
        orderId,
        body: JSON.stringify({ email: email.trim(), ...other }),
        callback: data => {
          if (data?.paymentType === 'stripe') {
            window.location.href = data?.url
          } else if (data?.paymentType === 'free') {
            navigate(`/success?orderId=${orderId}`)
          } else if (data?.paymentType === 'flywire') {
            const button = document.getElementById('checkout-button')

            let config = {
              env: `${process.env.REACT_APP_FLYWIRE_MODE}`,
              locale: 'en-EN',
              displayPayerInformation: true,
              provider: 'embed2.0',
              token: data?.token,
              return_url:
                process.env.REACT_APP_FLYWIRE_MODE === 'demo'
                  ? `https://immerse-v2-front-end.vercel.app/success?orderId=${orderId}&paymentType=flywire`
                  : `https://enrol.immerse.education/success?orderId=${orderId}&paymentType=flywire`
            }

            window.flywire.Checkout.render(config, '#checkout-button')

            setTimeout(() => {
              button.click()
            }, 100)
          }
          setIsLoading(false)
        },
        callbackRejected: errMessage => {
          toast.error(errMessage)
          setIsLoading(false)
        }
      })
    )
  }

  const handlePaymentSubmit = event => {
    const isAcademicValid = validateAcademicFields()
    if (!isAcademicValid) {
      event?.preventDefault()
      return
    }

    return handleSubmit(onSubmit)(event)
  }

  useEffect(() => {
    const validate = async () => {
      try {
        await OrderService.validate(orderId)
      } catch (error) {
        toast.error(errorCatch(error))
        const message = error?.response?.data?.message

        if (message === 'Coupon usage limit per person reached' || message === 'Coupon usage limit reached') {
          dispatch(
            removeCoupon({
              orderId: order.id
            })
          )
        }
      }
    }

    if (orderId) {
      validate()
    }
  }, [orderId])

  return (
    <WrapperForm>
      {isSmallScreen && (
        <Accordion
          className='accordion'
          disableGutters
          elevation={0}
          sx={{
            '&.Mui-expanded:first-of-type': {
              marginTop: '16px'
            }
          }}
        >
          <AccordionSummary expandIcon={<SelectAngleIcon />} className='accordion-summary'>
            <PriceBreakdownIcon />

            <Typography className='accordion-title'>Price Breakdown</Typography>

            <Typography className='accordion-title accordion-title-price'>
              £{new Intl.NumberFormat('en-IN').format(order.totalOrderAmount)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails className='accordion-details'>
            <PriceBreakdown isAccordion={true} />
          </AccordionDetails>
        </Accordion>
      )}

      <Form
        onSubmit={handlePaymentSubmit}
        footer={
          <>
            <CustomButton isLight onClick={() => navigate(-1)}>
              Back
            </CustomButton>
            <CustomButton disabled={isLoading} type='submit'>
              Submit Enrolment
            </CustomButton>
            <Button hidden id='checkout-button' />
          </>
        }
      >
        <div className='scrolled-area'>
          <Typography component='h3'>Billing address</Typography>

          <Box className='billing-card'>
            <Typography
              className={cn('billing-card-label', {
                'billing-card-label-error': errors?.firstName?.message || !!errors?.lastName?.message
              })}
            >
              Name on card
            </Typography>
            <div className='billing-card-line'>
              <Input
                value={firstName.value}
                onChange={firstName.onChange}
                error={errors?.firstName?.message}
                placeholder='First name'
              />
              <Input
                value={lastName.value}
                onChange={lastName.onChange}
                error={errors?.lastName?.message}
                placeholder='Last name'
              />
            </div>

            <Typography
              className={cn('billing-card-label', {
                'billing-card-label-error':
                  errors?.firstLineAddress?.message ||
                  errors?.city?.message ||
                  errors?.province?.message ||
                  errors?.country?.message ||
                  errors?.postcode?.message
              })}
            >
              Billing address
            </Typography>
            <div className='billing-card-line'>
              <Input
                value={firstLineAddress.value}
                onChange={firstLineAddress.onChange}
                error={errors?.firstLineAddress?.message}
                placeholder='First line address'
              />

              <Input
                value={secondLineAddress.value}
                onChange={secondLineAddress.onChange}
                placeholder='Second line address'
              />
            </div>

            <div className='billing-card-line'>
              <Input
                value={city.value}
                onChange={city.onChange}
                error={errors?.city?.message}
                placeholder='Town/City'
              />

              <Input
                value={county.value}
                onChange={county.onChange}
                error={errors?.province?.message}
                placeholder='County/Province'
              />
            </div>

            <div className='billing-card-line'>
              <Autocomplete
                className='form-item'
                error={errors?.country?.message}
                onSelect={selectedCountry => country.onChange(selectedCountry.label)}
                options={countries}
                placeholder='Country'
                initialValue={country.value}
                initialInputValue={country.value}
                renderOption={(props, option) => (
                  <Box component='li' sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                    <img
                      loading='lazy'
                      width='20'
                      srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                      src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                      alt=''
                    />
                    {option.label}
                  </Box>
                )}
              />

              <Input
                value={postCode.value}
                onChange={postCode.onChange}
                error={errors?.postcode?.message}
                placeholder='Postcode'
              />
            </div>

            <Input
              type='email'
              label='Billing email address'
              placeholder='Enter email'
              value={email.value}
              onChange={email.onChange}
              error={errors?.email?.message}
            />
          </Box>

          <Box className='form-col' sx={{ alignItems: 'flex-end' }}>
            <Select
              onChange={consultantRole.onChange}
              options={[
                'Education consultant',
                'Agent',
                'Teacher',
                'School counsellors',
                'Parent',
                'Family',
                'Friend',
                'Web search (e.g. Google)',
                'Social media',
                'Other'
              ]}
              className='form-item'
              label='How did you hear about Immerse Education?*'
              placeholder='How did you hear about us?'
              value={consultantRole.value}
              error={academicErrors?.roleOfConsultant?.message}
              sx={{ '.MuiInputBase-root': { minHeight: '48px', height: '48px' } }}
            />

            <Input
              label='Could you share exactly how you found out about Immerse?*'
              value={consultantFullName.value}
              onChange={consultantFullName.onChange}
              placeholder='Please provide details of where you heard about us'
              containerClass='form-item-full'
              error={academicErrors?.nameOfConsultantOrAdvisor?.message}
            />
          </Box>

          <FormControlLabel
            className={cn('checkbox-button', { checked: checkboxValue })}
            control={
              <Checkbox
                sx={{
                  color: '#D5DADF',
                  '&.Mui-checked': {
                    color: '#B8945C'
                  },
                  '& .MuiSvgIcon-root': { fontSize: 28 }
                }}
                checked={checkboxValue}
                onChange={handleReceiveEmails}
              />
            }
            label={
              <Typography className='checkbox-label'>
                If you are considering applying to study at Oxford/Cambridge and would like extra help (additional fees
                apply) with your application from our partner{' '}
                <Link
                  onClick={event => {
                    event.preventDefault()
                    window.open('https://www.uniadmissions.co.uk/', '', 'width=800, height=600')
                  }}
                  sx={{
                    fontWeight: 400,
                    '&:hover': {
                      color: '#9e681a !important'
                    }
                  }}
                >
                  UniAdmissions
                </Link>
                , check the box.
              </Typography>
            }
          />
        </div>
      </Form>
    </WrapperForm>
  )
}

export default PaymentScreen
